<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { html } from '@codemirror/lang-html';

export interface CodeMirrorExposedMethods {
  getContent: () => string;
  focus: () => void;
}

const props = defineProps<{ disabled?: boolean }>();

const modelValue = defineModel<string>({ required: true });

const editorView = ref<EditorView | null>(null);
const editorElement = ref<HTMLDivElement>();

// Create CodeMirror editor
const createEditor = () => {
  if (!editorElement.value) return;

  // Destroy existing editor if it exists
  if (editorView.value) {
    editorView.value.destroy();
  }

  editorView.value = new EditorView({
    doc: modelValue.value || '',
    extensions: [
      basicSetup,
      html(),
      EditorState.readOnly.of(props.disabled || false), // Now this will work
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !props.disabled) {
          const newContent = update.state.doc.toString();
          if (newContent !== modelValue.value) {
            modelValue.value = newContent;
          }
        }
      }),
    ],
    parent: editorElement.value,
  });
};

// Watch disabled prop and update editor
watch(
  () => props.disabled,
  () => {
    createEditor();
  }
);

// Update editor content when modelValue changes externally
watch(modelValue, (newValue) => {
  if (editorView.value && newValue !== editorView.value.state.doc.toString()) {
    const transaction = editorView.value.state.update({
      changes: {
        from: 0,
        to: editorView.value.state.doc.length,
        insert: newValue || '',
      },
    });
    editorView.value.dispatch(transaction);
  }
});

// Focus the editor when mounted
onMounted(() => {
  createEditor();
  if (editorView.value && !props.disabled) {
    editorView.value.focus();
  }
});

// Cleanup
onUnmounted(() => {
  if (editorView.value) {
    editorView.value.destroy();
  }
});

// Get content method for parent
const getContent = () => {
  return editorView.value?.state.doc.toString() || modelValue.value || '';
};

const focus = () => {
  if (!props.disabled) {
    editorView.value?.focus();
  }
};

// Expose methods to parent
defineExpose<CodeMirrorExposedMethods>({ getContent, focus });
</script>

<template>
  <div
    ref="editorElement"
    class="editor-wrapper"
    :class="{
      'tw-opacity-50 tw-cursor-not-allowed': disabled,
    }"
  />
</template>

<style>
.editor-wrapper {
  height: 100%;
  min-height: 200px;
}

.cm-editor {
  height: 100% !important;
  min-height: 200px;
}

.cm-scroller {
  height: 100% !important;
  min-height: 200px;
  overflow: auto !important;
}

.cm-content {
  min-height: 100%;
  padding-bottom: 0 !important;
}

.cm-gutters {
  height: 100% !important;
  min-height: 100% !important;
}

.cm-editor.cm-readOnly .cm-cursor {
  display: none !important;
}

.ͼ2 .cm-activeLine,
.ͼ2 .cm-activeLineGutter {
  background: var(--connx-primary-tint-85);
}
</style>
